'use client';

/**
 * useClubOffers - Hook for club-specific offers (v2)
 * Extends the offers functionality with club filtering
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { Offer, OfferSummary, OffersResource } from '../../core/resources/offers';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface ClubOfferItem {
    id: string;
    productId: string;
    variantId: string;
    product: {
        name: string;
        description: string;
    };
    variant: {
        name: string;
        description: string;
        imageUrl: string | null;
        grams: number;
    };
    unitAmount: number;
    quantity: number;
    amount: number;
    adjustedAmount: number;
}

export interface ClubOfferSummary {
    currency: string;
    totalAmount: number;
    totalAdjustedAmount: number;
    items: ClubOfferItem[];
}

export interface ClubOfferLineItem {
    id: string;
    quantity: number;
    price: {
        variant: {
            id: string;
            name: string;
            description: string | null;
            imageUrl: string | null;
            grams: number;
            product: {
                id: string;
                name: string;
                description: string;
            };
        };
    };
}

export interface ClubOffer {
    id: string;
    titleTrans: {
        en: string;
    };
    summaries: ClubOfferSummary[];
    offerLineItems: ClubOfferLineItem[];
}

export interface UseClubOffersOptions {
    /**
     * Whether to fetch club offers automatically on mount
     * @default true
     */
    enabled?: boolean;

    /**
     * Return URL for payment
     */
    returnUrl?: string;
}

export interface UseClubOffersResult {
    /**
     * Array of fetched club offers
     */
    offers: ClubOffer[];

    /**
     * Loading state
     */
    isLoading: boolean;

    /**
     * Error state
     */
    error: Error | null;

    /**
     * Refetch club offers
     */
    refetch: () => Promise<void>;

    /**
     * Get club offer by ID from the loaded offers
     */
    getOffer: (offerId: string) => ClubOffer | undefined;

    /**
     * Get total value of all club offers
     */
    getTotalValue: () => number;

    /**
     * Get total savings across all club offers
     */
    getTotalSavings: () => number;

    /**
     * Clear error state
     */
    clearError: () => void;

    /**
     * Pay for a club offer
     */
    payOffer: (offerId: string, returnUrl?: string) => Promise<void>;
}

/**
 * Transform generic Offer to ClubOffer format
 */
function transformToClubOffer(offer: Offer): ClubOffer {
    return {
        id: offer.id,
        titleTrans: offer.titleTrans as { en: string },
        summaries: offer.summaries.map((summary: OfferSummary) => ({
            currency: summary.currency,
            totalAmount: summary.totalAmount,
            totalAdjustedAmount: summary.totalAdjustedAmount,
            items: summary.items.map((item) => ({
                id: item.id,
                productId: item.id, // Using item ID as productId fallback
                variantId: item.id, // Using item ID as variantId fallback
                product: item.product,
                variant: {
                    name: item.variant.name,
                    description: '', // Not available in base Offer
                    imageUrl: item.variant.imageUrl,
                    grams: 0, // Not available in base Offer
                },
                unitAmount: item.unitAmount,
                quantity: item.quantity,
                amount: item.amount,
                adjustedAmount: item.adjustedAmount,
            })),
        })),
        offerLineItems: [], // Not available in base Offer
    };
}

export function useClubOffers(options: UseClubOffersOptions = {}): UseClubOffersResult {
    const { enabled = true, returnUrl } = options;
    const { storeId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();

    // Create offers resource client
    const offersResource = useMemo(() => {
        try {
            return new OffersResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize offers resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Create query key
    const queryKey = useMemo(() => ['club-offers', { storeId }], [storeId]);

    // Fetch club offers using TanStack Query
    const {
        data: offers = [],
        isLoading,
        error,
        refetch,
    } = useQuery<ClubOffer[]>({
        queryKey,
        enabled: enabled && !!storeId && isSessionInitialized,
        queryFn: async () => {
            if (!storeId) {
                throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
            }

            // Fetch all offers and filter for club type
            // Note: The API endpoint should support a 'type' parameter
            const response = await getGlobalApiClient().get<{ offers: Offer[] }>(
                `/api/v1/stores/${storeId}/offers`,
                {
                    params: {
                        type: 'club',
                    },
                }
            );

            // Transform to club offer format
            return (response.offers || []).map(transformToClubOffer);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // Pay offer mutation
    const payOfferMutation = useMutation({
        mutationFn: async ({ offerId, returnUrl }: { offerId: string; returnUrl?: string }) => {
            const url = returnUrl || (typeof window !== 'undefined' ? window.location.href : '');
            return await offersResource.payOffer(offerId);
        },
        onError: (error: Error) => {
            console.error('[SDK] Failed to pay offer:', error);
        },
    });

    // Helper functions
    const getOffer = useCallback(
        (offerId: string): ClubOffer | undefined => {
            return offers.find((offer) => offer.id === offerId);
        },
        [offers]
    );

    const getTotalValue = useCallback(() => {
        return offers.reduce((total, offer) => {
            const firstSummary = offer.summaries[0];
            return total + (firstSummary?.totalAdjustedAmount || 0);
        }, 0);
    }, [offers]);

    const getTotalSavings = useCallback(() => {
        return offers.reduce((total, offer) => {
            const firstSummary = offer.summaries[0];
            return total + (firstSummary?.totalAmount - firstSummary?.totalAdjustedAmount || 0);
        }, 0);
    }, [offers]);

    const clearError = useCallback(() => {
        // TanStack Query doesn't have a direct clearError method
        // Error will be cleared on next successful fetch
    }, []);

    const payOffer = useCallback(
        async (offerId: string, returnUrl?: string) => {
            await payOfferMutation.mutateAsync({ offerId, returnUrl });
        },
        [payOfferMutation]
    );

    return {
        offers,
        isLoading,
        error,
        refetch: async () => {
            await refetch();
        },
        getOffer,
        getTotalValue,
        getTotalSavings,
        clearError,
        payOffer,
    };
}

